import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeftRight, Plus, Trash2, Upload } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { BotaoModeloCsv, DialogoImportacao } from '../../componentes/importacao';
import { Botao, Cartao, ProvedorCartoesRecolhidos, Selo } from '../../componentes/base';
import { BotaoLixeira } from '../../componentes/lixeira';
import { campoProduto } from '../../componentes/cadastro-rapido';
import { Chatter } from '../../componentes/chatter';
import { CartaoVerticais, CampoVerticais, useVerticaisDaCriacao, verticaisDaCriacao } from '../../componentes/verticais';
import { Carregando, Vazio } from '../../componentes/estados';
import { Formulario, Selecao, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { del, get, mensagemDeErro, patch, post } from '../../nucleo/api';
import { data, dinheiro, nomePessoa } from '../../nucleo/formato';
import { CriarCamposDica } from '../../nucleo/criar-campo';
import { useSessao } from '../../nucleo/sessao';
import { invalidarEstoque, qtd, useUnidades } from './comum';
import { FormMovimentacao } from './Movimentacoes';
import { mascaraCest, mascaraNcm, type Produto, type SaldoDetalhado } from './tipos';
import { TextoCortado } from '../../componentes/texto-cortado';

type Situacao = '' | 'abaixo' | 'inativos';

/** M9.1: produtos com saldo, custo médio e valor em estoque. O drawer traz o formulário, o saldo e o chatter. */
export function Produtos() {
  const { t } = useTranslation('estoque');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const [busca, setBusca] = useSearchParams();
  const [situacao, setSituacao] = useState<Situacao>('');
  const [importar, setImportar] = useState(false);
  const [aberto, setAberto] = useState<Produto | 'novo' | null>(null);
  const [movimentar, setMovimentar] = useState<Produto | 'livre' | null>(null);

  // Link vindo da ficha do parceiro: ?produto=<id> abre o drawer.
  const idDoLink = busca.get('produto');
  const doLink = useQuery({ queryKey: ['/estoque/produtos', 'link', idDoLink], queryFn: () => get<Produto>(`/estoque/produtos/${idDoLink}`), enabled: !!idDoLink });
  useEffect(() => {
    if (doLink.data) {
      setAberto(doLink.data);
      setBusca({}, { replace: true });
    }
  }, [doLink.data, setBusca]);

  const colunas: Coluna<Produto>[] = [
    { chave: 'codigo', rotulo: t('campos.codigo'), mono: true, ordenavel: true },
    {
      chave: 'nome',
      rotulo: t('campos.nome'),
      principal: true,
      ordenavel: true,
      render: (p) => (
        <span className="flex min-w-0 flex-col">
          <span className="truncate font-medium" title={p.nome}>{p.nome}</span>
          {p.fornecedor_nome && <span className="truncate text-xs text-tinta-3" title={nomePessoa(p.fornecedor_nome)}>{nomePessoa(p.fornecedor_nome)}</span>}
        </span>
      ),
    },
    { chave: 'unidade', rotulo: t('campos.unidade') },
    {
      chave: 'saldo',
      rotulo: t('campos.saldo'),
      alinhar: 'direita',
      mono: true,
      ordenavel: true,
      render: (p) => <span className={p.abaixo_minimo ? 'text-laranja' : ''}>{qtd(p.saldo)}</span>,
    },
    { chave: 'estoque_minimo', rotulo: t('campos.minimo'), alinhar: 'direita', mono: true, oculta: true, render: (p) => qtd(p.estoque_minimo) },
    { chave: 'custo_centavos', rotulo: t('campos.custoMedio'), alinhar: 'direita', mono: true, render: (p) => dinheiro(p.custo_centavos, p.moeda) },
    { chave: 'preco_centavos', rotulo: t('campos.preco'), alinhar: 'direita', mono: true, oculta: true, render: (p) => dinheiro(p.preco_centavos, p.moeda) },
    { chave: 'valor', rotulo: t('campos.valorEstoque'), alinhar: 'direita', mono: true, ordenavel: true, render: (p) => dinheiro(p.valor_estoque_centavos, p.moeda) },
    {
      chave: 'situacao',
      rotulo: t('campos.situacao'),
      render: (p) =>
        !p.ativo ? <Selo>{t('status.inativo')}</Selo> : p.abaixo_minimo ? <Selo cor="laranja">{t('status.abaixoMinimo')}</Selo> : <Selo cor="verde">{t('status.ok')}</Selo>,
    },
    { chave: 'created_at', rotulo: t('campos.criadoEm'), oculta: true, mono: true, render: (p) => data(p.created_at) },
  ];
  return (
    <>
      <AcaoPrimaria>
        {/* Fase 2 (W25, K21): produto excluído volta pela lixeira (declarado no núcleo desde a W11, sem botão até aqui). */}
        {s.pode('estoque.excluir') && <BotaoLixeira entidades={['estoque.produto']} />}
        {s.pode('estoque.movimentar') && (
          <Botao icone={<ArrowLeftRight className="size-4" />} onClick={() => setMovimentar('livre')}>
            {t('acoes.novaMovimentacao')}
          </Botao>
        )}
        {/* Fase 2 (W6, L11): importação pelo framework do núcleo. Val5 (29/10): ação própria (`estoque.importar`). */}
        {s.pode('estoque.importar') && s.pode('estoque.criar') && s.pode('estoque.editar') && (
          <>
            <BotaoModeloCsv caminho="/estoque/produtos" />
            <Botao icone={<Upload className="size-4" />} onClick={() => setImportar(true)}>
              {tn('importacao.importar')}
            </Botao>
          </>
        )}
        {s.pode('estoque.criar') && (
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setAberto('novo')}>
            {t('acoes.novoProduto')}
          </Botao>
        )}
      </AcaoPrimaria>
      <Tabela<Produto>
        tela="estoque.produtos"
        caminho="/estoque/produtos"
        params={{ abaixo_minimo: situacao === 'abaixo' ? true : undefined, ativo: situacao === 'inativos' ? false : undefined }}
        filtros={{ situacao }}
        aoAplicarFiltros={(f) => setSituacao(f.situacao === 'abaixo' || f.situacao === 'inativos' ? f.situacao : '')}
        colunas={colunas}
        entidade="estoque.produto"
        aoClicar={(p) => setAberto(p)}
        vazio={<Vazio texto={t('vazio.produtos')} />}
        filtrosExtra={
          <div className="w-full sm:w-56">
            <Selecao
              aria-label={t('campos.situacao')}
              vazio={tn('comum.todos')}
              opcoes={[
                { valor: 'abaixo', rotulo: t('status.abaixoMinimo') },
                { valor: 'inativos', rotulo: t('status.inativo') },
              ]}
              value={situacao}
              onChange={(e) => setSituacao(e.target.value as Situacao)}
            />
          </div>
        }
      />
      {idDoLink && doLink.isPending && <Carregando />}
      <DrawerProduto
        aberto={aberto}
        aoFechar={() => setAberto(null)}
        aoMovimentar={(p) => {
          setAberto(null);
          setMovimentar(p);
        }}
      />
      <FormMovimentacao aberto={movimentar !== null} aoFechar={() => setMovimentar(null)} produto={movimentar && movimentar !== 'livre' ? movimentar : undefined} />
      <DialogoImportacao aberto={importar} aoFechar={() => setImportar(false)} caminho="/estoque/produtos" titulo={tn('importacao.importar')} />
    </>
  );
}

function DrawerProduto({ aberto, aoFechar, aoMovimentar }: { aberto: Produto | 'novo' | null; aoFechar: () => void; aoMovimentar: (p: Produto) => void }) {
  const { t } = useTranslation('estoque');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const unidades = useUnidades();
  const [excluir, setExcluir] = useState(false);
  const atual = aberto && aberto !== 'novo' ? aberto : null;
  // Ficha 2026-10-22: as verticais já na criação (null = o escopo de quem cria, como antes).
  const [verticais, setVerticais] = useVerticaisDaCriacao(aberto);

  // Recarrega o produto aberto para mostrar saldo e custo vivos depois de uma movimentação.
  const vivo = useQuery({ queryKey: ['/estoque/produtos', 'ficha', atual?.id], queryFn: () => get<Produto>(`/estoque/produtos/${atual!.id}`), enabled: !!atual });
  const p = vivo.data ?? atual;
  const saldo = useQuery({ queryKey: ['/estoque/produtos', 'saldo', atual?.id], queryFn: () => get<SaldoDetalhado>(`/estoque/produtos/${atual!.id}/saldo`), enabled: !!atual });

  const campos: DefCampo[] = [
    // Fase 2 (W25, K14): os campos essenciais vêm do núcleo (`campoProduto`), os mesmos do cadastro rápido no item da compra.
    campoProduto(t, 'codigo'),
    campoProduto(t, 'nome'),
    campoProduto(t, 'unidade', unidades),
    { nome: 'codigo_barras', rotulo: t('campos.codigoBarras'), tipo: 'texto', largura: 'meia' },
    {
      nome: 'fornecedor_padrao_id',
      rotulo: t('campos.fornecedorPadrao'),
      tipo: 'busca',
      largura: 'meia',
      busca: { caminho: '/cadastros/parceiros', params: { papel: 'fornecedor' }, rotulo: (i) => String(i.nome) },
    },
    campoProduto(t, 'custo_centavos'),
    campoProduto(t, 'preco_centavos'),
    { nome: 'estoque_minimo', rotulo: t('campos.minimo'), tipo: 'numero', min: 0, largura: 'terco', ajuda: t('ajuda.minimo') },
    { nome: 'controla_lote', rotulo: t('campos.controlaLote'), tipo: 'booleano', largura: 'meia', ajuda: t('ajuda.controlaLote') },
    { nome: 'ativo', rotulo: t('campos.ativo'), tipo: 'booleano', largura: 'meia' },
    { nome: 'descricao', rotulo: t('campos.descricao'), tipo: 'area' },
    // Lote 09/10 (B3): NCM e CEST num grupo Fiscal (por último: o título do grupo vale até o fim do formulário).
    { ...campoProduto(t, 'ncm'), largura: 'meia', grupo: t('campos.fiscal') },
    { nome: 'cest', rotulo: t('campos.cest'), tipo: 'texto', largura: 'meia', max: 20, placeholder: '00.000.00', ajuda: t('ajuda.cest'), grupo: t('campos.fiscal') },
  ];

  const remover = useMutation({
    mutationFn: () => del(`/estoque/produtos/${atual!.id}`),
    onSuccess: async () => {
      await invalidarEstoque(qc);
      toast.sucesso(tn('comum.removido'));
      setExcluir(false);
      aoFechar();
    },
    onError: (e) => {
      setExcluir(false);
      toast.erro(mensagemDeErro(e));
    },
  });

  return (
    <>
      <Drawer
        aberto={!!aberto}
        aoFechar={aoFechar}
        largura="lg"
        titulo={p ? `${p.codigo} ${p.nome}` : t('acoes.novoProduto')}
        rodape={
          <>
            {p && s.pode('estoque.excluir') && (
              <Botao variante="perigo" icone={<Trash2 className="size-4" />} onClick={() => setExcluir(true)} className="mr-auto">
                {tn('comum.excluir')}
              </Botao>
            )}
            {p && s.pode('estoque.movimentar') && (
              <Botao icone={<ArrowLeftRight className="size-4" />} onClick={() => aoMovimentar(p)}>
                {t('acoes.movimentar')}
              </Botao>
            )}
            {(p ? s.pode('estoque.editar') : s.pode('estoque.criar')) && (
              <>
                <Botao variante="fantasma" onClick={aoFechar}>
                  {tn('comum.cancelar')}
                </Botao>
                <Botao variante="primario" type="submit" form="form-produto">
                  {tn('comum.salvar')}
                </Botao>
              </>
            )}
          </>
        }
      >
        {p && (
          <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Resumo rotulo={t('campos.saldo')} valor={`${qtd(p.saldo)} ${p.unidade}`} cor={p.abaixo_minimo ? 'text-laranja' : ''} />
            <Resumo rotulo={t('campos.custoMedio')} valor={dinheiro(p.custo_centavos, p.moeda)} />
            <Resumo rotulo={t('campos.valorEstoque')} valor={dinheiro(p.valor_estoque_centavos, p.moeda)} />
            <Resumo rotulo={t('campos.minimo')} valor={qtd(p.estoque_minimo)} />
          </div>
        )}
        {/* Item 10 (21/10): cartão recolhido, como na ficha de Pessoas e empresas. */}
        {atual && (
          <ProvedorCartoesRecolhidos>
          <Cartao titulo={t('produto.onde')} className="mb-5" semPadding>
            {saldo.isPending ? (
              <Carregando />
            ) : !saldo.data?.linhas.length ? (
              <p className="px-4 py-3 text-sm text-tinta-3">{t('produto.semSaldo')}</p>
            ) : (
              <ul className="divide-y divide-borda text-sm">
                {saldo.data.linhas.map((l, i) => (
                  <li key={i} className="flex min-w-0 items-center justify-between gap-3 px-4 py-2">
                    <TextoCortado className="min-w-0">
                      {l.deposito_nome}
                      {l.variacao_sku && <span className="text-tinta-3">{`, ${l.variacao_sku}`}</span>}
                      {l.lote_codigo && <span className="text-tinta-3">{`, ${t('campos.lote')} ${l.lote_codigo}${l.validade ? ` (${data(l.validade)})` : ''}`}</span>}
                    </TextoCortado>
                    <span className="numero shrink-0">{qtd(l.saldo)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Cartao>
          </ProvedorCartoesRecolhidos>
        )}
        <CriarCamposDica entidade="estoque.produto" frase={t('produto.dicaCampos')} link={t('produto.criarNovosCampos')} />
        <Formulario
          key={atual?.id ?? 'novo'}
          id="form-produto"
          campos={campos}
          entidade="estoque.produto"
          inicial={(atual ? { ...atual, ncm: mascaraNcm(atual.ncm), cest: mascaraCest(atual.cest) } : { unidade: 'un', ativo: true, controla_lote: false, estoque_minimo: 0, custo_centavos: 0, preco_centavos: 0 }) as Record<string, unknown>}
          rotulosAtuais={atual ? { fornecedor_padrao_id: atual.fornecedor_nome ?? '' } : undefined}
          aoEnviar={async (v) => {
            const corpo = {
              codigo: v.codigo,
              nome: v.nome,
              descricao: v.descricao || null,
              unidade: v.unidade,
              codigo_barras: v.codigo_barras || null,
              ncm: (v.ncm as string | undefined)?.trim() || null,
              cest: (v.cest as string | undefined)?.trim() || null,
              custo_centavos: v.custo_centavos ?? 0,
              preco_centavos: v.preco_centavos ?? 0,
              estoque_minimo: Number(v.estoque_minimo ?? 0),
              controla_lote: !!v.controla_lote,
              ativo: v.ativo !== false,
              fornecedor_padrao_id: v.fornecedor_padrao_id || null,
              extras: v.extras ?? {},
            };
            if (atual) await patch(`/estoque/produtos/${atual.id}`, corpo);
            else await post('/estoque/produtos', { ...corpo, ...verticaisDaCriacao(verticais) });
            await invalidarEstoque(qc);
            toast.sucesso(tn('comum.salvo'));
            aoFechar();
          }}
        >
          {!atual && <CampoVerticais valor={verticais} aoMudar={setVerticais} />}
        </Formulario>
        {atual && (
          <div className="mt-6 border-t border-borda pt-4">
            <CartaoVerticais entidade="estoque.produto" registroId={atual.id} podeEditar={s.pode('estoque.editar')} />
            <Chatter entidade="estoque.produto" registroId={atual.id} titulo={`${atual.codigo} ${atual.nome}`} />
          </div>
        )}
      </Drawer>
      <Confirmar aberto={excluir} aoFechar={() => setExcluir(false)} aoConfirmar={() => remover.mutate()} carregando={remover.isPending} perigoso titulo={t('confirmar.excluirProduto')} texto={t('confirmar.excluirProdutoTexto')} />
    </>
  );
}

function Resumo({ rotulo, valor, cor = '' }: { rotulo: string; valor: string; cor?: string }) {
  return (
    <div className="min-w-0 rounded-lg border border-borda bg-sup-2 px-3 py-2">
      <p className="kicker truncate" title={rotulo}>{rotulo}</p>
      <p className={`numero mt-1 truncate text-base font-semibold ${cor}`} title={valor}>{valor}</p>
    </div>
  );
}
